import React, { useState, useEffect, useRef } from 'react';
import { useLSContext } from '../Context/useLSContext';

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  children: React.ReactNode;
  containerRef: React.RefObject<HTMLDivElement> | null;
  threshold?: number;
  resistance?: number;
  leaderboardRefresh?: boolean;
}

const PullToRefresh = ({ onRefresh, children, containerRef, threshold = 100, resistance = 2.5 }: PullToRefreshProps) => {
  const [startY, setStartY] = useState(0);
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const { appTheme } = useLSContext();
  const cRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = cRef.current;
    if (!element) return;

    const handleTouchStart = (e: TouchEvent) => {
      const element = cRef.current;
      if (!element) return;
      
      // Check both window and container scroll positions
      const isAtTop = containerRef?.current?.scrollTop === 0;
      
      if (isAtTop) {
        setStartY(e.touches[0].clientY);
        setPulling(true);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      const element = containerRef?.current;
      if (!pulling || !element) return;

      const isAtTop = element.scrollTop === 0;
      if (!isAtTop) {
        setPulling(false);
        return;
      }

      const y = e.touches[0].clientY;
      const distance = (y - startY) / resistance;

      if (distance > 0) {
        setPullDistance(distance);
        e.preventDefault();
      }
    };

    const handleTouchEnd = async () => {
      if (!pulling) return;

      if (pullDistance >= threshold) {
        await onRefresh();
      }

      setPulling(false);
      setPullDistance(0);
    };

    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [pulling, pullDistance, startY, onRefresh, resistance, threshold, containerRef]);

  return (
    <div
      ref={cRef}
      style={{ 
        position: 'relative',
        touchAction: 'pan-y pinch-zoom'
      }}
    >
      {pulling && (
        <div
          style={{
            position: 'absolute',
            top: -20,
            left: 0,
            right: 0,
            height: `${pullDistance}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: appTheme.palette.sepia.extraLight,
            transition: pulling ? 'none' : 'height 0.2s ease-out',
            zIndex: 1000
          }}
        >
          {pullDistance >= threshold ? 'Release to refresh' : 'Pull to refresh'}
        </div>
      )}
      {children}
    </div>
  );
};

export default PullToRefresh;